import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { useMe } from '../hooks/useMe'
import { useAIEditor } from '../hooks/useAIEditor'
import RichEditor, { type EditorToolbarState, type RichEditorHandle } from '../components/editor/RichEditor'
import { getMyProfileStory, updateMyProfileStory, updateMeProfile } from '../api/me'
import { uploadCoverDirect } from '../api/posts'
import { generatePeopleImage } from '../api/ai'
import { fetchPeopleReader } from '../api/peopleReader'
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiType,
  FiList,
  FiRotateCcw,
  FiRotateCw,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiLink,
  FiCode,
  FiMinus,
  FiImage,
  FiX,
} from 'react-icons/fi'
import { RiListOrdered, RiDoubleQuotesL, RiStrikethrough2, RiMarkPenLine } from 'react-icons/ri'
import { BiCodeBlock } from 'react-icons/bi'
import { MdFormatClear } from 'react-icons/md'
import MagazineHtml from '../components/renderers/MagazineHtml'

const BLANK_DOC = { type: 'doc', content: [{ type: 'paragraph', content: [] }] }

export default function PeopleEditor() {
  const { me } = useMe(true) // try to load even if token missing (cookies/dev)
  const authed = Boolean(getToken() || me?.id)
  const ai = useAIEditor()
  const [searchParams] = useSearchParams()
  const publicIdParam = searchParams.get('publicId') || searchParams.get('id') || searchParams.get('profile')

  const [storyTitle, setStoryTitle] = useState('')
  const [storySubtitle, setStorySubtitle] = useState('')
  const [coverUrl, setCoverUrl] = useState('')
  const [coverLandscapeUrl, setCoverLandscapeUrl] = useState('')
  const [storyHtml, setStoryHtml] = useState('<p></p>')
  const [storyPlain, setStoryPlain] = useState('')
  const [storyJson, setStoryJson] = useState<any>(BLANK_DOC)
  const [editorKey, setEditorKey] = useState(0)
  const [saving, setSaving] = useState(false)
  const [loadingStory, setLoadingStory] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [uploadingPortrait, setUploadingPortrait] = useState(false)
  const [uploadingLandscape, setUploadingLandscape] = useState(false)
  const [portraitLoadFailed, setPortraitLoadFailed] = useState(false)
  const [landscapeLoadFailed, setLandscapeLoadFailed] = useState(false)
  const [previewLoadFailed, setPreviewLoadFailed] = useState(false)
  const portraitInputRef = useRef<HTMLInputElement | null>(null)
  const landscapeInputRef = useRef<HTMLInputElement | null>(null)

  // AI assist
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiSuggestion, setAiSuggestion] = useState<string>('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [tone, setTone] = useState<'Friendly' | 'Professional' | 'Playful' | 'Creator' | 'Academic'>('Friendly')
  const [viewer, setViewer] = useState<'public' | 'follower'>('public')
  const [device, setDevice] = useState<'mobile' | 'desktop'>('mobile')
  const [pixePrompt, setPixePrompt] = useState('')
  const [pixeImages, setPixeImages] = useState<string[]>([])
  const [selectedGenerated, setSelectedGenerated] = useState<string | null>(null)
  const [pixeLoading, setPixeLoading] = useState(false)
  const [pixeRefs, setPixeRefs] = useState<{ url: string; b64: string; mime: string }[]>([])
  const [applyingGenerated, setApplyingGenerated] = useState(false)
  const [tb, setTb] = useState<EditorToolbarState>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    code: false,
    highlight: false,
    link: false,
    h1: false,
    h2: false,
    h3: false,
    h4: false,
    bullet: false,
    ordered: false,
    quote: false,
    codeBlock: false,
    align: 'left',
    canUndo: false,
    canRedo: false,
  })

  const richRef = useRef<RichEditorHandle | null>(null)

  useEffect(() => {
    let cancelled = false
    async function hydrateFromStoryApi() {
      const data = await getMyProfileStory()
      if (cancelled) return
      setStoryTitle(data.title || '')
      setStorySubtitle(data.subtitle || '')
      setCoverUrl(data.cover_url || (me as any)?.profile?.cover_url || '')
      setCoverLandscapeUrl(data.cover_landscape_url || '')
      const htmlFromApiRaw = (data.body_html && data.body_html.trim()) ? data.body_html.trim() : ''
      const htmlFromApi = htmlFromApiRaw && htmlFromApiRaw !== '<p></p>' ? htmlFromApiRaw : ''
      const htmlFallback = data.body_plain ? markdownishToHtml(data.body_plain) : ''
      // Legacy fallback: pull legacy about fields if story is empty
      const legacyMd = (me as any)?.profile?.about_long_md || ''
      const legacyShort = (me as any)?.profile?.about_short || ''
      const legacyHtml = (me as any)?.profile?.about_long_html || ''
      const legacyHtmlResolved = legacyHtml ? legacyHtml.trim() : (legacyMd ? markdownishToHtml(legacyMd) : '')
      const resolvedHtml = htmlFromApi || htmlFallback || legacyHtmlResolved || '<p></p>'
      const resolvedPlain = (data.body_plain || legacyMd || legacyShort || htmlToPlain(legacyHtmlResolved) || '').trim()

      setStoryHtml(resolvedHtml || legacyHtmlResolved)
      setStoryPlain(resolvedPlain || htmlToPlain(resolvedHtml || legacyHtmlResolved))
      const json = data.body_json && Object.keys(data.body_json || {}).length ? data.body_json : BLANK_DOC
      setStoryJson(json)
      setEditorKey((key) => key + 1)
    }

    async function hydrateFromReader(pid: string) {
      const reader = await fetchPeopleReader(pid)
      if (cancelled) return
      const story = reader.story || {}
      const about = reader.about || {}
      setStoryTitle(story.title || '')
      setStorySubtitle(story.subtitle || '')
      setCoverUrl(reader.hero?.coverImage || '')
      setCoverLandscapeUrl(reader.hero?.coverImage || '')
      const html =
        (story.body?.html && story.body.html.trim() && story.body.html.trim() !== '<p></p>' ? story.body.html.trim() : '') ||
        (about.long?.html && about.long.html.trim() ? about.long.html.trim() : '') ||
        (story.body?.plain ? markdownishToHtml(story.body.plain) : '') ||
        (about.long?.plain ? markdownishToHtml(about.long.plain) : '') ||
        '<p></p>'
      const plain =
        (story.body?.plain && story.body.plain.trim()) ||
        (about.long?.plain && about.long.plain.trim()) ||
        (html ? htmlToPlain(html) : '') ||
        ''
      setStoryHtml(html)
      setStoryPlain(plain)
      setStoryJson((story.body?.json && Object.keys(story.body?.json || {}).length ? story.body.json : BLANK_DOC))
      setEditorKey((key) => key + 1)
      setMessage('Loaded bio from public profile')
    }

    async function load() {
      setLoadingStory(true)
      setError(null)
      const myPublicId = (me as any)?.profile?.public_id
      try {
        if (authed) {
          await hydrateFromStoryApi()
          // If still essentially empty, pull from public profile as a secondary source
          if ((richRef.current?.getText()?.trim() || storyPlain.trim() || storyHtml.replace(/<[^>]+>/g, '').trim()) === '' && (myPublicId || publicIdParam)) {
            await hydrateFromReader(myPublicId || publicIdParam!)
          }
        } else if (myPublicId) {
          await hydrateFromReader(myPublicId)
        } else if (publicIdParam) {
          await hydrateFromReader(publicIdParam)
        } else {
          setError('Log in to load your story, or pass ?publicId= to preview.')
        }
      } catch (e: any) {
        if (cancelled) return
        // Fall back to any legacy bio if the API call fails (e.g., expired auth)
        const legacyMd = (me as any)?.profile?.about_long_md || ''
        const legacyHtml = (me as any)?.profile?.about_long_html || ''
        const legacyShort = (me as any)?.profile?.about_short || ''
        const resolvedLegacyHtml =
          (legacyHtml && legacyHtml.trim() ? legacyHtml.trim() : '') ||
          (legacyMd ? markdownishToHtml(legacyMd) : '')
        if (legacyMd || legacyShort || legacyHtml) {
          setStoryHtml(resolvedLegacyHtml || '<p></p>')
          setStoryPlain((legacyMd || legacyShort || htmlToPlain(resolvedLegacyHtml) || '').trim())
          setStoryJson(BLANK_DOC)
          setEditorKey((key) => key + 1)
        }
        const msg = e?.response?.status === 401
          ? 'Session expired — please log in again.'
          : e?.response?.data?.detail || e?.message || 'Failed to load your story'
        setError(msg)
        const myPublicId = (me as any)?.profile?.public_id
        if (!authed && (publicIdParam || myPublicId)) {
          // Try public reader as a backup if auth failed
          try { await hydrateFromReader(myPublicId || publicIdParam!) } catch { /* ignore */ }
        }
      } finally {
        if (!cancelled) setLoadingStory(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [authed, me?.id, me?.profile?.public_id, publicIdParam])

  const previewCover = useMemo(() => {
    const profileCover = (me as any)?.profile?.cover_url || ''
    return coverLandscapeUrl || coverUrl || profileCover
  }, [coverLandscapeUrl, coverUrl, me])
  const portraitSrcValid = isRenderableImageUrl(coverUrl)
  const landscapeSrcValid = isRenderableImageUrl(coverLandscapeUrl)
  const previewSrcValid = isRenderableImageUrl(previewCover)

  useEffect(() => {
    setPortraitLoadFailed(false)
  }, [coverUrl])

  useEffect(() => {
    setLandscapeLoadFailed(false)
  }, [coverLandscapeUrl])

  useEffect(() => {
    setPreviewLoadFailed(false)
  }, [previewCover])

  const onEditorChange = (html: string, plain: string) => {
    setStoryHtml(html)
    setStoryPlain(plain)
  }

  const onJsonChange = (json: any) => {
    setStoryJson(json || BLANK_DOC)
  }

  async function onRunAI() {
    const prompt = (aiPrompt || '').trim()
    if (!prompt) return
    setError(null)
    try {
      const res = await ai.run({ prompt, body: storyPlain })
      const text = (res as any)?.content || ''
      setAiSuggestion(text)
      setSuggestions([])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  async function onSuggestThree() {
    setError(null)
    setAiSuggestion('')
    try {
      const base = storyPlain?.trim()
      const who = `${me?.profile?.display_name || me?.username || 'a creator'}`
      const instruction = `Write ${base ? 'three new sections' : 'three story drafts'} for ${who} in a ${tone.toLowerCase()} tone. ${base ? 'Use the current story as context.' : 'Create fresh angles.'} Keep each 60–160 words. Output exactly three options separated by a line with three dashes (---).`
      const res = await ai.run({ prompt: instruction, body: base || undefined })
      const text = (res as any)?.content || ''
      const parts = String(text).split(/\n\s*---\s*\n/).map(s => s.trim()).filter(Boolean)
      setSuggestions(parts.length ? parts.slice(0, 3) : [text])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  async function onQuick(action: 'Tighten' | 'Expand' | 'Punchy' | 'Add CTA') {
    if (!storyPlain?.trim()) return
    setError(null)
    try {
      const map: Record<string, string> = {
        Tighten: `Rewrite the story to be shorter, clearer, and ${tone.toLowerCase()} while keeping key details.`,
        Expand: `Expand the story with a few more details and sensory texture, keeping a ${tone.toLowerCase()} tone.`,
        Punchy: `Rewrite the story to be punchy, energetic, and ${tone.toLowerCase()} with a strong opener.`,
        'Add CTA': `Rewrite the story to include a friendly one-sentence call-to-action at the end. Keep tone ${tone.toLowerCase()}.`,
      }
      const res = await ai.run({ prompt: map[action], body: storyPlain })
      const text = (res as any)?.content || ''
      setAiSuggestion(text)
      setSuggestions([])
    } catch (e: any) {
      setError(e?.message || ai.error || 'AI request failed')
    }
  }

  function replaceWithText(text: string) {
    if (!text) return
    const html = textToHtmlParagraphs(text)
    richRef.current?.setHTML(html)
    setStoryHtml(html)
    setStoryPlain(text)
    setStoryJson(richRef.current?.getJSON() || BLANK_DOC)
  }

  function appendText(text: string) {
    if (!text) return
    richRef.current?.appendHTML(textToHtmlParagraphs(text))
  }

  function applyReplace() {
    if (!aiSuggestion) return
    replaceWithText(aiSuggestion)
    setAiSuggestion('')
  }

  function applyAppend() {
    if (!aiSuggestion) return
    appendText(aiSuggestion)
    setAiSuggestion('')
  }

  async function onSave() {
    if (!authed) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const safeTitle = (storyTitle || '').slice(0, 200)
      const safeSubtitle = (storySubtitle || '').slice(0, 200)
      if (safeTitle !== storyTitle) setStoryTitle(safeTitle)
      if (safeSubtitle !== storySubtitle) setStorySubtitle(safeSubtitle)
      // Prevent saving giant data URLs; allow hosted URLs up to 1000 chars (DB limit)
      const isDataUrl = (u: string) => u.startsWith('data:')
      if ((coverUrl && isDataUrl(coverUrl)) || (coverLandscapeUrl && isDataUrl(coverLandscapeUrl))) {
        setError('Please upload the image (no data URLs) before saving.')
        setSaving(false)
        return
      }
      if ((coverUrl && coverUrl.length > 1000) || (coverLandscapeUrl && coverLandscapeUrl.length > 1000)) {
        setError('Image URLs must be under 1000 characters.')
        setSaving(false)
        return
      }
      const doc = storyJson ?? richRef.current?.getJSON() ?? BLANK_DOC
      const data = await updateMyProfileStory({
        title: safeTitle,
        subtitle: safeSubtitle,
        cover_url: coverUrl || '',
        cover_landscape_url: coverLandscapeUrl || '',
        body_json: doc,
        body_plain: storyPlain || '',
        schema_version: 1,
      })
      // Also sync profile cover so feeds that read profile.cover_url update immediately
      if (coverUrl) {
        try {
          const meRes = await updateMeProfile({ cover_url: coverUrl })
          const newCover = meRes?.profile?.cover_url
          if (newCover) setCoverUrl(newCover)
        } catch (err: any) {
          setError(err?.response?.data?.detail || err?.message || 'Saved story, but failed to update profile cover.')
        }
      }
      setMessage('Saved ✓')
      showToast('People profile saved')
      setStoryJson(data.body_json || doc)
      setStoryHtml(data.body_html || storyHtml)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const disableSave = !authed || saving || loadingStory

  const refreshToolbar = () => {
    richRef.current?.refreshToolbarState()
  }

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }
  const syncEditor = () => {
    const html = richRef.current?.getHTML() || '<p></p>'
    const text = richRef.current?.getText()?.replace(/\u00A0/g, ' ').trim() || ''
    const json = richRef.current?.getJSON() || BLANK_DOC
    setStoryHtml(html)
    setStoryPlain(text)
    setStoryJson(json)
  }
  function toggleBold() { richRef.current?.toggleBold(); refreshToolbar() }
  function toggleItalic() { richRef.current?.toggleItalic(); refreshToolbar() }
  function toggleStrike() { richRef.current?.toggleStrike(); refreshToolbar() }
  function toggleCode() { richRef.current?.toggleCode(); refreshToolbar() }
  function toggleUnderline() { richRef.current?.toggleUnderline(); refreshToolbar() }
  function setH1() { richRef.current?.setHeading(1); refreshToolbar() }
  function setH2() { richRef.current?.setHeading(2); refreshToolbar() }
  function setH3() { richRef.current?.setHeading(3); refreshToolbar() }
  function setH4() { richRef.current?.setHeading(4); refreshToolbar() }
  function toggleBullet() { richRef.current?.toggleBulletList(); refreshToolbar() }
  function toggleOrdered() { richRef.current?.toggleOrderedList(); refreshToolbar() }
  function toggleQuote() { richRef.current?.toggleBlockquote(); refreshToolbar() }
  function toggleHighlight() { richRef.current?.toggleHighlight?.(); refreshToolbar() }
  function toggleCodeBlock() { richRef.current?.toggleCodeBlock(); refreshToolbar() }
  function insertDivider() { richRef.current?.insertDivider(); syncEditor(); refreshToolbar() }
  function alignLeft() { richRef.current?.setTextAlign('left'); refreshToolbar() }
  function alignCenter() { richRef.current?.setTextAlign('center'); refreshToolbar() }
  function alignRight() { richRef.current?.setTextAlign('right'); refreshToolbar() }
  function alignJustify() { richRef.current?.setTextAlign('justify'); refreshToolbar() }
  function undo() { richRef.current?.undo(); syncEditor(); refreshToolbar() }
  function redo() { richRef.current?.redo(); syncEditor(); refreshToolbar() }
  function clearFormatting() { richRef.current?.clearFormatting(); refreshToolbar() }

  function addLink() {
    const url = window.prompt('Enter URL (https://...)')?.trim()
    if (!url) return
    const hasSelection = !!richRef.current?.getSelectionText()
    if (hasSelection) {
      richRef.current?.setLink(url)
    } else {
      const text = window.prompt('Link text (optional)')?.trim() || url
      const safeText = escapeHtml(text)
      const safeUrl = escapeHtml(url)
      richRef.current?.appendHTML(`<a href="${safeUrl}" rel="noopener noreferrer nofollow" target="_blank">${safeText}</a>`)
    }
    syncEditor()
  }

  function removeLink() {
    richRef.current?.unsetLink()
    syncEditor()
  }

  function addImage() {
    const url = window.prompt('Image URL (https://...)')?.trim()
    if (!url) return
    const alt = window.prompt('Alt text (optional)')?.trim() || ''
    richRef.current?.insertImage(url, alt)
    syncEditor()
  }

  async function handleUpload(file: File, kind: 'portrait' | 'landscape') {
    const setUploading = kind === 'portrait' ? setUploadingPortrait : setUploadingLandscape
    setUploading(true)
    setError(null)
    try {
      const uploaded = await uploadCoverDirect(file, {
        upload_content_type: 'profile_image',
        target_field: 'cover_url',
        object_type: 'uploads.people_image',
      })
      const url = uploaded?.public_url
      if (!url) throw new Error('Upload did not return a URL')
      if (kind === 'portrait') setCoverUrl(url)
      else setCoverLandscapeUrl(url)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function onPortraitFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleUpload(file, 'portrait')
    e.target.value = ''
  }

  function onLandscapeFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleUpload(file, 'landscape')
    e.target.value = ''
  }

  // Pixe image generator (AI) using People bio
  async function fileToBase64(file: File): Promise<{ b64: string; mime: string }> {
    const buf = await file.arrayBuffer()
    const bytes = new Uint8Array(buf)
    let binary = ''
    bytes.forEach((b) => { binary += String.fromCharCode(b) })
    return { b64: btoa(binary), mime: file.type || 'image/jpeg' }
  }

  async function dataUrlToFile(dataUrl: string, filename = 'image.png'): Promise<File> {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    return new File([blob], filename, { type: blob.type || 'image/png' })
  }

  async function applyGeneratedImage(src: string, target: 'both' | 'portrait' | 'landscape' = 'both', silent = false) {
    try {
      setApplyingGenerated(true)
      let finalUrl = src
      if (src.startsWith('data:')) {
        const file = await dataUrlToFile(src, 'pixe-generated.png')
        const uploaded = await uploadCoverDirect(file, {
          upload_content_type: 'profile_image',
          target_field: 'cover_url',
          object_type: 'uploads.people_image',
        })
        if (!uploaded?.public_url) throw new Error('Upload failed')
        finalUrl = uploaded.public_url
      }
      if (target === 'both' || target === 'portrait') setCoverUrl(finalUrl)
      if (target === 'both' || target === 'landscape') setCoverLandscapeUrl(finalUrl)
      // Persist immediately so feeds refresh without waiting for Save
      try {
        const meRes = await updateMeProfile({ cover_url: finalUrl })
        const newCover = meRes?.profile?.cover_url
        if (newCover) setCoverUrl(newCover)
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Applied image locally, but failed to save cover.')
      }
      if (!silent) setMessage('Applied generated image')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to apply generated image')
    } finally {
      setApplyingGenerated(false)
    }
  }

  function buildPixePrompt(): string {
    const bio = (storyPlain || '').trim()
    const custom = (pixePrompt || '').trim()
    const subtitle = (storySubtitle || '').trim()
    const title = (storyTitle || '').trim()
    const base = [custom, bio || subtitle || title].filter(Boolean).join('\n\n')
    const safety = [
      'Use all provided reference images together; do not ignore any reference.',
      'Do not place any text, words, logos, watermarks, or typography in the image.',
      'Produce a magazine-quality portrait collage inspired by the bio and references only.',
    ].join(' ')
    return `${base}\n\n${safety}`.trim()
  }

  async function onGeneratePixeImage(file?: File) {
    const prompt = buildPixePrompt()
    if (!prompt) {
      setError('Add a short bio prompt first.')
      return
    }
    setPixeLoading(true)
    setError(null)
    try {
      let sourceImage: string | undefined
      let sourceImageMime: string | undefined
      let references = [...pixeRefs]
      if (file) {
        const { b64, mime } = await fileToBase64(file)
        const dataUrl = `data:${mime};base64,${b64}`
        references = [...references, { url: dataUrl, b64, mime }]
        sourceImage = b64
        sourceImageMime = mime
      } else if (references.length) {
        sourceImage = references[0].b64
        sourceImageMime = references[0].mime
      }
      const refImagesPayload = references.map((ref) => ({
        image_b64: ref.b64,
        image_mime: ref.mime,
      }))
      const res = await generatePeopleImage({
        prompt,
        sourceImage,
        sourceImageMime,
        size: '1024x1024',
        responseFormat: 'b64_json',
        referenceImages: refImagesPayload.length ? refImagesPayload : undefined,
      })
      const imgs = (res.images || []).map((img: any) => {
        if (img.url) return img.url
        if (img.b64_json) return `data:image/png;base64,${img.b64_json}`
        return ''
      }).filter(Boolean) as string[]
      setPixeImages(imgs)
      if (imgs[0]) {
        setSelectedGenerated(imgs[0])
        await applyGeneratedImage(imgs[0], 'both', true)
      }
      setMessage('Generated a new Pixe image')
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 409) {
        setError('Image generator is busy — please try again in a few seconds.')
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Generation failed')
      }
    } finally {
      setPixeLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />

      <main className="mx-auto max-w-7xl px-4 py-6">
        {!authed && (
          <div className="mb-4 text-sm text-amber-300">
            Please <Link className="underline" to="/login">log in</Link> to edit your People Pixe.
          </div>
        )}

        <div className="mb-4">
          <h1 className="text-xl font-extrabold">Edit People Pixe</h1>
          {message && <div className="text-sm text-white/70 mt-1">{message}</div>}
          {error && <div className="text-sm text-red-400 mt-1">{error}</div>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Editor */}
          <section className="lg:col-span-7 space-y-4">
            <div className="rounded-xl border border-white/10 p-4 space-y-3">
              <div className="font-semibold">Story details / Bio</div>
              <input
                value={storyTitle}
                onChange={(e)=>setStoryTitle(e.target.value)}
                placeholder="Story/Bio title (optional)"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand"
              />
              <input
                value={storySubtitle}
                onChange={(e)=>setStorySubtitle(e.target.value)}
                placeholder="Subtitle or hook (optional)"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div className="rounded-xl border border-white/10 p-4">
              <div className="font-semibold mb-2">Story body / Bio</div>
              {loadingStory ? (
                <div className="text-sm text-white/70">Loading editor...</div>
              ) : (
                <>
                  <div className="relative text-sm px-2 pt-2 pb-1 rounded-xl bg-neutral-900/60 border border-white/10 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Bold" aria-label="Bold" onClick={toggleBold} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bold ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiBold /></button>
                        <button title="Italic" aria-label="Italic" onClick={toggleItalic} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.italic ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiItalic /></button>
                        <button title="Strikethrough" aria-label="Strikethrough" onClick={toggleStrike} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.strike ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiStrikethrough2 /></button>
                        <button title="Inline code" aria-label="Inline code" onClick={toggleCode} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.code ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiCode /></button>
                        <button title="Underline" aria-label="Underline" onClick={toggleUnderline} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.underline ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiUnderline /></button>
                      </div>
                      <div className="w-px h-6 bg-white/10" />
                      <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Heading 1" aria-label="Heading 1" onClick={setH1} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h1 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H1</span></button>
                        <button title="Heading 2" aria-label="Heading 2" onClick={setH2} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h2 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H2</span></button>
                        <button title="Heading 3" aria-label="Heading 3" onClick={setH3} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h3 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H3</span></button>
                        <button title="Heading 4" aria-label="Heading 4" onClick={setH4} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h4 ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiType /><span className="text-[11px]">H4</span></button>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-nowrap items-center gap-1.5">
                      <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Bullet list" aria-label="Bullet list" onClick={toggleBullet} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.bullet ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiList /></button>
                        <button title="Numbered list" aria-label="Numbered list" onClick={toggleOrdered} className={`inline-flex items-center justify-center h-8 rounded-md px-2.5 border ${tb.ordered ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiListOrdered /></button>
                        <button title="Quote" aria-label="Quote" onClick={toggleQuote} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.quote ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><RiDoubleQuotesL /></button>
                        <button title="Highlight" aria-label="Highlight" onClick={toggleHighlight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.highlight ? 'bg-amber-500/30 border-amber-400/40 text-amber-100' : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'}`}><RiMarkPenLine /></button>
                        <button title="Code block" aria-label="Code block" onClick={toggleCodeBlock} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.codeBlock ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><BiCodeBlock /></button>
                        <button title="Divider" aria-label="Divider" onClick={insertDivider} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiMinus /></button>
                      </div>
                      <div className="w-px h-6 bg-white/10" />
                      <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Align left" aria-label="Align left" onClick={alignLeft} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'left' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignLeft /></button>
                        <button title="Align center" aria-label="Align center" onClick={alignCenter} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'center' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignCenter /></button>
                        <button title="Align right" aria-label="Align right" onClick={alignRight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'right' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignRight /></button>
                        <button title="Justify" aria-label="Justify" onClick={alignJustify} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'justify' ? 'bg-brand/60 border-brand/40 text-white ring-1 ring-brand/50' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/90'}`}><FiAlignJustify /></button>
                      </div>
                      <div className="w-px h-6 bg-white/10" />
                      <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Link" aria-label="Link" onClick={addLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiLink /></button>
                        <button title="Unlink" aria-label="Unlink" onClick={removeLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiX /></button>
                        <button title="Image" aria-label="Image" onClick={addImage} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><FiImage /></button>
                      </div>
                      <div className="ml-auto flex items-center gap-1.5 shrink-0 bg-white/5 rounded-lg px-1 py-1">
                        <button title="Undo" aria-label="Undo" onClick={undo} disabled={!tb.canUndo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCcw /></button>
                        <button title="Redo" aria-label="Redo" onClick={redo} disabled={!tb.canRedo} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 disabled:opacity-50"><FiRotateCw /></button>
                        <button title="Clear formatting" aria-label="Clear formatting" onClick={clearFormatting} className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/90"><MdFormatClear /></button>
                      </div>
                    </div>
                  </div>
                  <RichEditor
                    key={editorKey}
                    ref={richRef}
                    initialHTML={storyHtml}
                    onChange={onEditorChange}
                    onJsonChange={onJsonChange}
                    onToolbarState={(state) => setTb(state)}
                    className="mt-2"
                  />
                </>
              )}

              {/* Tone presets + quick actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                {(['Friendly','Professional','Playful','Creator','Academic'] as const).map(t => (
                  <button key={t} onClick={()=>setTone(t)} className={`px-2.5 py-1 text-xs rounded-full border ${tone===t ? 'bg-blue-500 border-transparent' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                    {t}
                  </button>
                ))}
                <span className="ml-1 text-xs text-white/60">Tone</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(['Tighten','Expand','Punchy','Add CTA'] as const).map(a => (
                  <button key={a} onClick={()=>onQuick(a)} disabled={ai.loading} className="px-2.5 py-1 text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60">
                    {a}
                  </button>
                ))}
                <button onClick={onSuggestThree} disabled={ai.loading} className="ml-auto px-3 py-1.5 text-xs rounded-full bg-blue-500 text-white disabled:opacity-60">Suggest 3</button>
              </div>

              {/* Advanced prompt */}
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={aiPrompt}
                  onChange={(e)=>setAiPrompt(e.target.value)}
                  placeholder={`Advanced prompt (uses ${tone.toLowerCase()} tone)...`}
                  className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button onClick={onRunAI} disabled={ai.loading} className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60">
                  {ai.loading ? 'Thinking...' : 'Run' }
                </button>
              </div>

              {aiSuggestion && (
                <div className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/10">
                  <div className="px-3 py-2 border-b border-blue-500/20 flex items-center justify-between">
                    <div className="text-sm font-semibold text-white/90">AI suggestion</div>
                    <div className="flex items-center gap-2">
                      <button onClick={applyReplace} className="px-2 py-1 text-xs rounded-md bg-blue-500 text-white">Replace</button>
                      <button onClick={applyAppend} className="px-2 py-1 text-xs rounded-md border border-white/10 bg-white/5">Append</button>
                    </div>
                  </div>
                  <div className="p-3 text-sm text-white/90 whitespace-pre-wrap">{aiSuggestion}</div>
                </div>
              )}

              {suggestions.length > 0 && (
                <div className="mt-3 space-y-3">
                  {suggestions.map((s, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-white/5">
                      <div className="px-3 py-2 border-b border-white/10 text-xs text-white/70">Option {i+1}</div>
                      <div className="p-3 text-sm text-white/90 whitespace-pre-wrap">{s}</div>
                      <div className="px-3 py-2 border-t border-white/10 flex items-center gap-2">
                        <button onClick={()=>replaceWithText(s)} className="px-2 py-1 text-xs rounded-md bg-blue-500 text-white">Use</button>
                        <button onClick={()=>appendText(s)} className="px-2 py-1 text-xs rounded-md border border-white/10 bg-white/5">Append</button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <button onClick={onSuggestThree} disabled={ai.loading} className="px-3 py-1.5 text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60">Re-roll</button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Pixe image</div>
                  <div className="text-xs text-white/70">Use a portrait (3:4) plus an optional landscape crop (16:9) for your header.</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between h-7 mb-1">
                    <div className="text-xs text-white/60 whitespace-nowrap">Portrait (3:4)</div>
                    {coverUrl && (
                      <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={()=>setCoverUrl('')}>Remove</button>
                    )}
                  </div>
                  {coverUrl && portraitSrcValid && !portraitLoadFailed ? (
                    <img
                      src={coverUrl}
                      alt="Portrait cover"
                      className="w-full h-40 object-cover rounded-lg border border-white/10"
                      onError={() => setPortraitLoadFailed(true)}
                    />
                  ) : (
                    <div className="w-full h-40 rounded-lg border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-xs text-white/50">Paste a portrait image URL</div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      ref={portraitInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onPortraitFileChange}
                    />
                    <button
                      type="button"
                      onClick={()=>portraitInputRef.current?.click()}
                      disabled={uploadingPortrait}
                      className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60"
                    >
                      {uploadingPortrait ? 'Uploading...' : 'Upload image'}
                    </button>
                    <span className="text-xs text-white/40">or paste URL</span>
                  </div>
                  <input
                    value={coverUrl}
                    onChange={(e)=>setCoverUrl(e.target.value)}
                    placeholder="https:// portrait URL"
                    className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
                <div className="md:col-span-3">
                  <div className="flex items-center justify-between h-7 mb-1">
                    <div className="text-xs text-white/60 whitespace-nowrap">Landscape (16:9)</div>
                    {coverLandscapeUrl && (
                      <button type="button" className="text-xs rounded px-2 py-1 border border-white/15 text-white/80 hover:bg-white/10" onClick={()=>setCoverLandscapeUrl('')}>Clear</button>
                    )}
                  </div>
                  {coverLandscapeUrl && landscapeSrcValid && !landscapeLoadFailed ? (
                    <img
                      src={coverLandscapeUrl}
                      alt="Landscape cover"
                      className="w-full h-40 object-cover rounded-lg border border-white/10"
                      onError={() => setLandscapeLoadFailed(true)}
                    />
                  ) : (
                    <div className="w-full h-40 rounded-lg border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-xs text-white/50">Optional landscape variation</div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      ref={landscapeInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLandscapeFileChange}
                    />
                    <button
                      type="button"
                      onClick={()=>landscapeInputRef.current?.click()}
                      disabled={uploadingLandscape}
                      className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60"
                    >
                      {uploadingLandscape ? 'Uploading...' : 'Upload image'}
                    </button>
                    <span className="text-xs text-white/40">or paste URL</span>
                  </div>
                  <input
                    value={coverLandscapeUrl}
                    onChange={(e)=>setCoverLandscapeUrl(e.target.value)}
                    placeholder="https:// landscape URL"
                    className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
              </div>
              <div className="mt-3 text-xs text-white/70">Need new art? Generate a cover inside Publish, then paste the URLs here.</div>

              {/* AI Pixe image generator */}
              <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">Generate a Pixe image</div>
                    <div className="text-xs text-white/60">Use your bio + an optional photo to create a fresh header.</div>
                  </div>
                  {pixeLoading && (
                    <div className="flex items-center gap-2 text-xs text-white/70">
                      <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-transparent animate-spin" />
                      Working...
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <textarea
                    value={pixePrompt}
                    onChange={(e)=>setPixePrompt(e.target.value)}
                    placeholder="Short bio or vibe to guide the image..."
                    rows={3}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="pixe-ref"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : []
                      files.forEach(async (f) => {
                        const { b64, mime } = await fileToBase64(f)
                        const dataUrl = `data:${mime};base64,${b64}`
                        setPixeRefs((prev) => [...prev, { url: dataUrl, b64, mime }])
                      })
                      if (e.target) e.target.value = ''
                    }}
                  />
                  <label htmlFor="pixe-ref" className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/85 hover:bg-white/10 cursor-pointer">
                    Upload reference images
                  </label>
                  <button
                    type="button"
                    onClick={() => onGeneratePixeImage()}
                    disabled={pixeLoading}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                  >
                    {pixeLoading ? 'Generating...' : 'Generate Pixe image'}
                  </button>
                  {pixeImages.length > 0 && <span className="text-xs text-white/60">{pixeImages.length} result{pixeImages.length>1?'s':''}</span>}
                </div>
                {pixeRefs.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-white/60 mb-1">Reference images sent to AI</div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                      {pixeRefs.map((ref, idx) => (
                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
                          <img src={ref.url} alt="Reference" className="absolute inset-0 h-full w-full object-cover" />
                          <button
                            type="button"
                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => setPixeRefs((prev) => prev.filter((_, i) => i !== idx))}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {pixeImages.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {pixeImages.map((src, i) => (
                        <button
                          key={i}
                          onClick={()=> setSelectedGenerated(src)}
                          className={`relative aspect-[4/5] rounded-lg overflow-hidden border ${selectedGenerated===src ? 'border-brand ring-1 ring-brand' : 'border-white/10 hover:border-brand'} disabled:opacity-60`}
                          title="Select this image"
                          disabled={applyingGenerated}
                        >
                          <img src={src} alt="Generated Pixe" className="absolute inset-0 h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={!selectedGenerated || applyingGenerated}
                        onClick={()=> selectedGenerated && applyGeneratedImage(selectedGenerated, 'portrait')}
                        className="px-3 py-1.5 text-xs rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60"
                      >
                        Use as portrait
                      </button>
                      <button
                        type="button"
                        disabled={!selectedGenerated || applyingGenerated}
                        onClick={()=> selectedGenerated && applyGeneratedImage(selectedGenerated, 'landscape')}
                        className="px-3 py-1.5 text-xs rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-60"
                      >
                        Use as landscape
                      </button>
                      <button
                        type="button"
                        disabled={!selectedGenerated || applyingGenerated}
                        onClick={()=> selectedGenerated && applyGeneratedImage(selectedGenerated, 'both')}
                        className="px-3 py-1.5 text-xs rounded-lg bg-brand text-white hover:bg-brand-600 disabled:opacity-60"
                      >
                        Use for both
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  // Clear unsaved changes back to a blank state; keeping it simple vs reload
                  setStoryTitle('')
                  setStorySubtitle('')
                  setCoverUrl('')
                  setCoverLandscapeUrl('')
                  setStoryHtml('<p></p>')
                  setStoryPlain('')
                  setStoryJson(BLANK_DOC)
                  setEditorKey((k) => k + 1)
                  setMessage('Changes cleared')
                  showToast('Edits canceled')
                }}
                className="px-4 py-2 rounded-lg border border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={disableSave}
                className="px-4 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </section>

          {/* Preview */}
          <aside className="lg:col-span-5">
            <div className="mb-2 flex items-center justify-between">
              <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
                <button onClick={()=>setViewer('public')} className={`px-2 py-1 text-xs rounded-full ${viewer==='public' ? 'bg-white/10' : ''}`}>Public</button>
                <button onClick={()=>setViewer('follower')} className={`px-2 py-1 text-xs rounded-full ${viewer==='follower' ? 'bg-white/10' : ''}`}>Follower</button>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
                <button onClick={()=>setDevice('mobile')} className={`px-2 py-1 text-xs rounded-full ${device==='mobile' ? 'bg-white/10' : ''}`}>Mobile</button>
                <button onClick={()=>setDevice('desktop')} className={`px-2 py-1 text-xs rounded-full ${device==='desktop' ? 'bg-white/10' : ''}`}>Desktop</button>
              </div>
            </div>

            <div className={`${device==='mobile' ? 'mx-auto w-[360px] md:w-[420px]' : ''} rounded-xl border border-white/10 overflow-hidden mb-4`}>
              <div className="relative aspect-[16/9]">
                {previewCover && previewSrcValid && !previewLoadFailed ? (
                  <img
                    src={previewCover}
                    alt="Cover"
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => setPreviewLoadFailed(true)}
                  />
                ) : (
                  <div className="absolute inset-0 bg-white/5" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                <div className="absolute left-4 bottom-4 flex items-center gap-3">
                  {me?.profile?.avatar_url ? (
                    <img src={me.profile.avatar_url} className="h-12 w-12 rounded-full object-cover border border-white/20" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-white/10" />
                  )}
                  <div>
                    <div className="text-white font-extrabold">{me?.profile?.display_name || me?.username || 'You'}</div>
                    <div className="text-white/80 text-xs">{me?.profile?.handle ? `@${me.profile.handle}` : ''}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${device==='mobile' ? 'mx-auto w-[360px] md:w-[420px]' : ''} rounded-xl border border-white/10 p-4`}>
              <div className="font-semibold mb-2">Story preview</div>
              {storyTitle && (
                <div className="mb-1 text-lg font-extrabold text-white">{storyTitle}</div>
              )}
              {storySubtitle && (
                <div className="mb-3 text-white/75 text-sm">{storySubtitle}</div>
              )}
              {storyHtml && storyHtml.trim() !== '<p></p>' ? (
                <MagazineHtml surface="peoplePreview" html={storyHtml} />
              ) : (
                <div className="text-white/70 text-sm">Your story preview will appear here...</div>
              )}
            </div>
          </aside>
        </div>
      </main>
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-white/10 bg-neutral-900/90 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  )
}

function isRenderableImageUrl(value?: string | null): boolean {
  const raw = String(value || '').trim()
  if (!raw) return false
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) return true
  if (raw.startsWith('/api/')) return true
  if (raw.startsWith('/media/')) return true
  return false
}

function markdownishToHtml(text: string): string {
  const normalized = (text || '').replace(/\r\n/g, '\n')
  if (!normalized.trim()) return '<p></p>'

  const inline = (s: string) => {
    let safe = escapeHtml(s)
    safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    safe = safe.replace(/(^|[^*])\*(.+?)\*(?!\*)/g, '$1<em>$2</em>')
    safe = safe.replace(/`([^`]+)`/g, '<code>$1</code>')
    return safe
  }

  const blocks = normalized.split(/\n{2,}/)
  const htmlBlocks = blocks.map((block) => {
    const b = block.trim()
    if (!b) return ''
    if (/^#{1,6}\s+/.test(b)) {
      const level = Math.min(6, (b.match(/^#+/)?.[0].length || 1))
      const content = b.replace(/^#{1,6}\s+/, '')
      return `<h${level}>${inline(content)}</h${level}>`
    }
    const lines = b.split('\n')
    if (lines.length > 1 && lines.every((l) => /^[-*]\s+/.test(l.trim()))) {
      const items = lines
        .map((l) => l.trim().replace(/^[-*]\s+/, ''))
        .map((l) => `<li>${inline(l)}</li>`)
        .join('')
      return `<ul>${items}</ul>`
    }
    return `<p>${inline(b.replace(/\n/g, ' '))}</p>`
  }).filter(Boolean)

  return htmlBlocks.join('')
}

function htmlToPlain(html: string): string {
  if (!html) return ''
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+\n/g, '\n')
    .trim()
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
